import { lazy, object, string, type ObjectSchema } from "yup";
import { isUSA } from "../../common/utils";
import {
  isValidBsb,
  isValidEftInstitutionNumber,
  isValidEftTransitNumber,
  isValidIbanMod97,
  isValidSortCode,
} from "../../utils/bankValidators";
import { isIbanCountry } from "../../utils/ibanCountries";
import { swiftCodeBaseSchema } from "../PaymentPrefConsolidated/Components/BankValidators/SwiftCodeInput/constants";
import {
  AddressInputSchemaGenerator,
  type AddressInputType,
} from "../SharedAddressInput/constants";

// ---------------------------------------------------------------------------
// Country / Currency
// ---------------------------------------------------------------------------

export enum CountryCurrencyStepInputFields {
  Country = "country",
  Currency = "currency",
}

export type CountryCurrencyStepFormValues = {
  [CountryCurrencyStepInputFields.Country]: string;
  [CountryCurrencyStepInputFields.Currency]: string;
};

const countryCurrencyStepSchema: ObjectSchema<CountryCurrencyStepFormValues> = object({
  [CountryCurrencyStepInputFields.Country]: string().required("Country is required"),
  [CountryCurrencyStepInputFields.Currency]: string().required("Currency is required"),
});

// ---------------------------------------------------------------------------
// Bank & Account
// ---------------------------------------------------------------------------

export enum BankAndAccountStepInputFields {
  SwiftBic = "swiftBic",
  BankNameIntl = "bankNameIntl",
  AccountNumberOrIban = "accountNumberOrIban",
}

export enum LocalRoutingInputFields {
  SortCode = "sortCode",
  Bsb = "bsb",
  TransitNumber = "transitNumber",
  InstitutionNumber = "institutionNumber",
}

export type BankAndAccountStepFormValues = {
  [BankAndAccountStepInputFields.SwiftBic]: string;
  [BankAndAccountStepInputFields.BankNameIntl]?: string;
  [BankAndAccountStepInputFields.AccountNumberOrIban]: string;
  [LocalRoutingInputFields.SortCode]?: string;
  [LocalRoutingInputFields.Bsb]?: string;
  [LocalRoutingInputFields.TransitNumber]?: string;
  [LocalRoutingInputFields.InstitutionNumber]?: string;
};

const LOCAL_ROUTING_RULES: Record<
  LocalRoutingInputFields,
  { label: string; invalid: string; isValid: (value: string) => boolean }
> = {
  [LocalRoutingInputFields.SortCode]: {
    label: "Sort code",
    invalid: "Enter a valid 6-digit sort code",
    isValid: isValidSortCode,
  },
  [LocalRoutingInputFields.Bsb]: {
    label: "BSB code",
    invalid: "Enter a valid 6-digit BSB code",
    isValid: isValidBsb,
  },
  [LocalRoutingInputFields.TransitNumber]: {
    label: "Transit number",
    invalid: "Enter a valid 5-digit transit number",
    isValid: isValidEftTransitNumber,
  },
  [LocalRoutingInputFields.InstitutionNumber]: {
    label: "Institution number",
    invalid: "Enter a valid 3-digit institution number",
    isValid: isValidEftInstitutionNumber,
  },
};

export const LOCAL_ROUTING_FIELDS_BY_COUNTRY: Readonly<
  Record<string, readonly LocalRoutingInputFields[]>
> = {
  GB: [LocalRoutingInputFields.SortCode],
  CA: [LocalRoutingInputFields.TransitNumber, LocalRoutingInputFields.InstitutionNumber],
  AU: [LocalRoutingInputFields.Bsb],
};

export const localRoutingFieldsForCountry = (
  country?: string,
  usAccount?: boolean,
): readonly LocalRoutingInputFields[] =>
  usAccount ? [] : (country && LOCAL_ROUTING_FIELDS_BY_COUNTRY[country]) || [];

export const accountFieldLabel = (country?: string): string =>
  isIbanCountry(country) ? "Account number / IBAN" : "Account number";

export const buildBankAndAccountStepSchema = (
  country?: string,
  usAccount?: boolean,
): ObjectSchema<BankAndAccountStepFormValues> => {
  const label = accountFieldLabel(country);

  const accountSchema = isIbanCountry(country)
    ? string()
        .required(`${label} is required`)
        .test("iban-mod97", "Enter a valid IBAN", (value) => isValidIbanMod97(value))
    : string().required(`${label} is required`);

  const localRouting = Object.fromEntries(
    localRoutingFieldsForCountry(country, usAccount).map((field) => {
      const rule = LOCAL_ROUTING_RULES[field];
      return [
        field,
        string()
          .required(`${rule.label} is required`)
          .test(field, rule.invalid, (value) => rule.isValid(value)),
      ];
    }),
  );

  return object({
    [BankAndAccountStepInputFields.SwiftBic]: swiftCodeBaseSchema.required(
      "Swift / BIC code is required",
    ),
    [BankAndAccountStepInputFields.BankNameIntl]: string(),
    [BankAndAccountStepInputFields.AccountNumberOrIban]: accountSchema,
    [LocalRoutingInputFields.SortCode]: string(),
    [LocalRoutingInputFields.Bsb]: string(),
    [LocalRoutingInputFields.TransitNumber]: string(),
    [LocalRoutingInputFields.InstitutionNumber]: string(),
    ...localRouting,
  });
};

// ---------------------------------------------------------------------------
// Owner
// ---------------------------------------------------------------------------

export enum OwnerStepFields {
  OwnerName = "ownerName",
  OwnerAddress = "ownerAddress",
  Notes = "notes",
}

export type OwnerStepValues = {
  [OwnerStepFields.OwnerName]: string;
  [OwnerStepFields.OwnerAddress]: AddressInputType;
  [OwnerStepFields.Notes]: string;
};

const ownerAddressRequiredSchema = AddressInputSchemaGenerator({
  zipRequiredForNonUS: false,
});

const ownerAddressContentFields = Object.keys(ownerAddressRequiredSchema.fields).filter(
  (field) => field !== "sameAsMailingAddress",
);

const isOwnerAddressBlank = (value: unknown) =>
  value == null ||
  ownerAddressContentFields.every(
    (field) => String((value as Record<string, unknown>)[field] ?? "").trim() === "",
  );

const ownerAddressOptionalSchema = lazy((value) =>
  isOwnerAddressBlank(value) ? object() : ownerAddressRequiredSchema,
);

export const buildOwnerStepSchema = (addressRequired: boolean): ObjectSchema<OwnerStepValues> =>
  object({
    [OwnerStepFields.OwnerName]: string()
      .required("Owner name is required")
      .matches(/\S/, "Owner name cannot be just spaces")
      .max(60, "Owner name must be 60 characters or fewer"),
    [OwnerStepFields.Notes]: string().defined().default(""),
    [OwnerStepFields.OwnerAddress]: addressRequired
      ? ownerAddressRequiredSchema
      : ownerAddressOptionalSchema,
  });

// ---------------------------------------------------------------------------
// Merged form schema
// ---------------------------------------------------------------------------

export type BankAccountFormValues = CountryCurrencyStepFormValues &
  BankAndAccountStepFormValues &
  OwnerStepValues;

export type BuildBankAccountFormSchemaArgs = {
  country?: string;
  usCentricMode: boolean;
};

export const buildBankAccountFormSchema = ({
  country,
  usCentricMode,
}: BuildBankAccountFormSchemaArgs): ObjectSchema<BankAccountFormValues> => {
  const bankSchema = buildBankAndAccountStepSchema(country, usCentricMode);
  const ownerSchema = buildOwnerStepSchema(!usCentricMode && !isUSA(country));
  const countryCurrencySchema = usCentricMode
    ? object({
        [CountryCurrencyStepInputFields.Country]: string().default(""),
        [CountryCurrencyStepInputFields.Currency]: string().default(""),
      })
    : countryCurrencyStepSchema;

  return countryCurrencySchema
    .concat(bankSchema)
    .concat(ownerSchema) as unknown as ObjectSchema<BankAccountFormValues>;
};
